/**
 * ContratoMarcoRepo sobre Supabase. Misma lógica que DexieContratoMarcoRepo.
 * Las tarifas van embebidas en la entidad ContratoMarco (payload), no en
 * tabla aparte.
 */

import type { ContratoMarcoRepo } from "../../repos/ContratoMarcoRepo";
import type { ContratoMarco, Tarifa } from "@app-types/entities";
import type { EstadoContrato } from "@constants/estados";
import { puedeTransicionarContrato } from "@/features/contratos/contratosRules";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseContratoMarcoRepo
  extends SupabaseBaseRepo<ContratoMarco>
  implements ContratoMarcoRepo
{
  constructor(tenantId: string) {
    super("contrato", tenantId);
  }

  async list(filter?: Partial<ContratoMarco>): Promise<ContratoMarco[]> {
    const all = await this.listAll();
    if (filter?.estado) return all.filter((c) => c.estado === filter.estado);
    if (filter?.contratistaId) return all.filter((c) => c.contratistaId === filter.contratistaId);
    return all.sort((a, b) => b.vigenciaFin.localeCompare(a.vigenciaFin));
  }

  async listByContratista(contratistaId: string): Promise<ContratoMarco[]> {
    return (await this.listAll()).filter((c) => c.contratistaId === contratistaId);
  }

  async listVigentes(): Promise<ContratoMarco[]> {
    return (await this.listAll()).filter((c) => c.estado === "VIGENTE");
  }

  async listByEstado(estado: EstadoContrato): Promise<ContratoMarco[]> {
    return (await this.listAll()).filter((c) => c.estado === estado);
  }

  async get(id: string): Promise<ContratoMarco | null> {
    return this.getById(id);
  }

  async create(data: Omit<ContratoMarco, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const nuevo: ContratoMarco = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<ContratoMarco>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Contrato ${id} not found`);
    const updated: ContratoMarco = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async changeState(id: string, nextState: EstadoContrato, _userEmail: string): Promise<void> {
    const c = await this.get(id);
    if (!c) throw new Error(`Contrato ${id} not found`);
    if (!puedeTransicionarContrato(c.estado, nextState)) {
      throw new Error(`Transicion ${c.estado} -> ${nextState} no permitida`);
    }
    await this.update(id, { estado: nextState }, c.version);
  }

  async getSaldoDisponible(id: string): Promise<number> {
    const c = await this.get(id);
    return c?.montoTecho ?? 0;
  }

  async getTarifas(contratoId: string): Promise<Tarifa[]> {
    const c = await this.get(contratoId);
    return c?.tarifas ?? [];
  }
}
